/* eslint-disable complexity */
import 'react-tabs/style/react-tabs.css'
import classes from './Tabs.module.scss'
import { Children } from 'react'
import {
  Tabs as ReactTabs,
  Tab,
  TabList,
  TabPanel
} from 'react-tabs'
import type { ReactNode } from 'react'

export interface BaseProps {
  children: ReactNode[]
  tabLabels: ReactNode[]
  isDisabled?: boolean
  showTabLabels?: boolean
  externalQuery?: ReactNode
  variant?: 'templateFlow' | 'autoFlow'
}

export interface VisibilityPanelProps extends BaseProps {
  onTabIndexChange: (value: number) => void
  selectedTabIndex: number
  isInvisiblePanel: true
}

export interface NonInvisibleProps extends BaseProps {
  onTabIndexChange?: (value: number) => void
  selectedTabIndex?: number
  isInvisiblePanel?: false
}

type Props = NonInvisibleProps | VisibilityPanelProps

const visibilityPanelStyle = (tabIndex: number, selectedTab?: number) => tabIndex === selectedTab
  ? classes.TabPanelChildrenVisible
  : classes.TabPanelChildrenInvisible

const Tabs = ({
  children,
  tabLabels,
  showTabLabels = true,
  onTabIndexChange,
  selectedTabIndex,
  isDisabled = false,
  isInvisiblePanel = false,
  externalQuery,
  variant = 'templateFlow'
}: Props) => {
  const smallerPaddingStyle = variant === 'autoFlow'
    ? classes.hasNoMinHeight
    : undefined

  const disabledStyle = isDisabled
    ? classes.DisabledTab
    : undefined

  const tabLabelAlignStyle = variant === 'autoFlow'
    ? classes[variant + 'TabLabel']
    : undefined

  const tabClassName = [
    classes.Tab,
    smallerPaddingStyle,
    disabledStyle,
    tabLabelAlignStyle
  ].filter(tabClassNames => tabClassNames).join(' ')

  const tabListClassName = [
    classes.TabList,
    classes[variant]
  ].filter(tabListClassNames => tabListClassNames).join(' ')

  return (
    <ReactTabs
      className={classes.Tabs}
      onSelect={
        onTabIndexChange
          ? (index: number) => onTabIndexChange(index)
          : undefined
      }
      selectedIndex={selectedTabIndex}
      selectedTabClassName={classes.TabSelected}
    >
      {
        showTabLabels &&
          <TabList className={tabListClassName}>
            {
              tabLabels.map((label, index) =>
                <Tab
                  className={tabClassName}
                  disabled={isDisabled}
                  key={index}
                >
                  <span className={classes.TabLabel}>
                    {label}
                  </span>
                </Tab>
              )
            }
            {
              externalQuery
                ? (
                  <li className={classes.ExternalQueryTabWrapper}>
                    <div className={classes.ExternalQueryWrapper}>
                      {externalQuery}
                    </div>
                  </li>
                )
                : null
            }
          </TabList>
      }
      {
        Children.map(children, (child, index) =>
          <TabPanel
            className={classes.TabPanel}
            disabled={isDisabled}
            forceRender={isInvisiblePanel}
            key={index}
            selectedClassName={classes.TabPanelSelected}
          >
            <div
              className={
                isInvisiblePanel
                  ? visibilityPanelStyle(index, selectedTabIndex)
                  : classes.TabPanelChildrenBase
              }
            >
              {child}
            </div>
          </TabPanel>
        )
      }
    </ReactTabs>
  )
}

export default Tabs
